<!--
  The PR header's agent button, beside Review, and the one place the pull
  request's local agents are dealt with on its page. The button says the
  state at a glance: an agent asking to connect (Approve, in the accent),
  else the agents reading it and how they are (their presence, or not
  heard from lately), else Connect agent. It opens the Local agents panel:
  first what waits on the person, the approval cards and what came of the
  last one (relayAgents.host keeps them out of the app's dock, so nothing
  lies over the header), then RelayPrAgents: connecting one, or status,
  scope and revoking. The panel follows the state as it changes: an
  approval going leaves the agent it connected in the list below.

  An approval arriving opens the panel by itself, unless the person is
  typing somewhere (the panel would take the focus): the button says so
  instead. Opening asks the agents' computers again, so the panel is
  current. The presences are the tab's (relayAgents.checkPresence), which
  the Overview's card shares.
-->
<script lang="ts">
  import { relayAgents, type Notice } from '../state/relayAgents.svelte';
  import { agentButtonText, type AgentState } from '../logic/relayText';
  import Popover from './Popover.svelte';
  import RelayApprovalCard from './RelayApprovalCard.svelte';
  import RelayPrAgents from './RelayPrAgents.svelte';
  import { tick, untrack } from 'svelte';
  import { pullKey, sameSubject } from '../api/relaySubject';
  let { owner, repo, number }: { owner: string; repo: string; number: number } = $props();
  const ref = $derived(pullKey(owner, repo, number));

  $effect(() => relayAgents.host(ref));

  /** The agents whose grant here is in force, each with how it is. */
  const reading = $derived(
    relayAgents.grants.filter((g) => sameSubject(g.subject, ref) && g.status === 'granted').flatMap((g) => {
      const c = relayAgents.connection(g.connection);
      if (!c) return [];
      const p = relayAgents.presence[c.id];
      const state: AgentState =
        p === undefined || p === 'checking'
          ? relayAgents.stale(c.id) ? 'quiet' : 'checking'
          : !p.reachable ? 'unreachable'
          : p.presence === 'listening' || p.presence === 'wakeable' ? 'listening'
          : p.presence === 'offline' ? (relayAgents.stale(c.id) ? 'quiet' : 'offline')
          : 'connected';
      return [{ name: c.name, state }];
    }),
  );
  /** Grants here its computer refused: the panel says what to do. */
  const refused = $derived(relayAgents.grantsOn(ref).some((g) => g.status === 'refused'));
  /** Agents asking to read this pull request, waiting on the person. */
  const asking = $derived(relayAgents.approvalsFor(ref));
  const order = $derived(asking.map((a) => a.key).join('\n'));
  const text = $derived(agentButtonText(reading, refused, asking.map((a) => a.name ?? 'An agent')));
  /** What came of the last approval here, until it clears or is dismissed. */
  const notice = $derived.by(() => {
    const n = relayAgents.notice;
    return n?.subject && sameSubject(n.subject, ref) ? n : null;
  });

  $effect(() => {
    void relayAgents.workerDown;
    const ids = relayAgents.grantsOn(ref).filter((g) => g.status === 'granted').map((g) => g.connection);
    untrack(() => { for (const id of ids) relayAgents.checkPresence(id); });
  });

  let anchor: HTMLButtonElement | null = $state(null);
  let open = $state(false);
  function show() {
    open = true;
    // Opening asks again: what the panel shows is current.
    for (const g of relayAgents.grantsOn(ref)) if (g.status === 'granted') relayAgents.checkPresence(g.connection, true);
  }
  function close(refocus: boolean) {
    open = false;
    if (refocus) anchor?.focus();
  }
  /** Whether the person is typing somewhere, where opening the panel would take their focus. */
  const typing = () => {
    const a = document.activeElement as HTMLElement | null;
    return !!a && (a.isContentEditable || (a.matches('input, textarea, select') && !(a as HTMLInputElement).readOnly));
  };
  // A new approval opens the panel (once each), unless the person is typing.
  let offered = new Set<string>();
  $effect(() => {
    const keys = asking.map((a) => a.key);
    // Not before the button is there to open it from (the pairings still being read).
    if (!anchor) return;
    untrack(() => {
      const fresh = keys.filter((k) => !offered.has(k));
      offered = new Set(keys);
      if (fresh.length && !open && !typing()) show();
    });
  });

  // The notice clears by itself after a while (relayAgents.expire), unless the pointer or focus is on it.
  let line: HTMLElement | undefined = $state();
  $effect(() => {
    const n: Notice | null = notice;
    if (!n) return;
    return relayAgents.expire(n, () => !!line?.matches(':hover, :focus-within'));
  });
  /** Focus for when what held it in the panel went away: the next card, else the panel's heading. */
  async function refocus() {
    await tick();
    if (document.activeElement && document.activeElement !== document.body) return;
    document.querySelector<HTMLElement>('.agents-panel .relay-approval h3, .agents-panel .relay-agents h3')?.focus();
  }
</script>

{#if relayAgents.loaded}
  <button
    bind:this={anchor}
    class="btn agents-btn"
    class:ask={text.tone === 'ask'}
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-label={text.label}
    title={text.label}
    onclick={() => (open ? close(false) : show())}
  ><span class="dot {text.tone}" aria-hidden="true"></span>{text.short}</button>
  {#if open && anchor}
    <Popover {anchor} role="dialog" label="Local agents" initial={asking.length ? '.relay-approval h3' : undefined} onclose={close}>
      <div class="agents-panel">
        {#if asking.length}
          <div class="approvals" role="region" aria-label="Agent connections to approve">
            {#each asking as approval (approval.key)}<RelayApprovalCard {approval} {order} onleave={refocus} />{/each}
          </div>
        {/if}
        {#if notice}
          <p class="notice" class:alert={notice.tone === 'alert'} class:warn={notice.persist} role="status" tabindex="-1" bind:this={line}>
            <span>{notice.text}</span>
            <button class="btn small" onclick={() => { relayAgents.dismissNotice(); void refocus(); }}>Dismiss</button>
          </p>
        {/if}
        <RelayPrAgents subject={ref} embedded />
      </div>
    </Popover>
  {/if}
{/if}

<style>
  .agents-btn { display: inline-flex; align-items: center; gap: 7px; max-width: 16em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .agents-btn.ask { border-color: var(--accent); color: var(--accent); font-weight: 600; }
  .dot { flex: none; width: 8px; height: 8px; border-radius: 50%; background: transparent; box-shadow: inset 0 0 0 1.5px var(--muted); }
  .dot.live { background: var(--good); box-shadow: none; }
  .dot.idle { background: var(--warn); box-shadow: none; }
  .dot.off { background: var(--muted); box-shadow: none; opacity: .6; }
  .dot.ask { background: var(--accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 30%, transparent); }
  .agents-panel { width: min(420px, calc(100vw - 48px)); padding: 12px; display: grid; gap: 12px; }
  .approvals { display: grid; gap: 10px; }
  .approvals :global(.relay-approval) { box-shadow: none; }
  .notice { display: flex; gap: 10px; align-items: center; justify-content: space-between; margin: 0; padding: 8px 10px; border: 1px solid var(--accent); border-radius: 8px; font-size: 13px; }
  .notice.alert { border-color: var(--bad); }
  .notice.warn { border-color: var(--warn); background: var(--warnbg); }
  .notice > span { min-width: 0; overflow-wrap: anywhere; }
  .notice .btn { flex: none; }
</style>
